import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { Identifier } from "./identifier-pools-model";

export class IdentifierPoolsValidators {
  public static alphaNumericRegex = /^[a-zA-Z0-9]+$/;
  public static digitsOnlyRegex = /^\d+$/;
  public static cusipRegex = /^\d{3}[a-zA-Z]*\d$/;
  public static ricRegex = /^(?=[a-zA-Z0-9]*=[a-zA-Z0-9]*$)[a-zA-Z0-9=]+$/;
  public static isinRegex = /^[A-Z]{2}[A-Z0-9]{9}[0-9A-Z]$/;

  public static validatorsMap: Record<string, ValidatorFn> = {
    'common code': Validators.pattern(IdentifierPoolsValidators.alphaNumericRegex),
    'valoren': Validators.compose([
      Validators.pattern(IdentifierPoolsValidators.digitsOnlyRegex),
      Validators.maxLength(9),
      Validators.minLength(9)
    ]),
    'wkn': Validators.compose([
      Validators.pattern(IdentifierPoolsValidators.alphaNumericRegex),
      Validators.maxLength(6),
      Validators.minLength(6),
    ]),
    'isin': Validators.compose([
      IdentifierPoolsValidators.isinValidator(),
      Validators.pattern(IdentifierPoolsValidators.isinRegex),
    ]),
    'ric': Validators.pattern(IdentifierPoolsValidators.ricRegex),
    'cusip': Validators.pattern(IdentifierPoolsValidators.cusipRegex),
  };

  public static duplicateTypeInGroupValidator(control: AbstractControl): ValidationErrors | null {
    if (!control || !control.value || !control.value?.identifiers) {
      return null;
    }

    const duplicateType = Object.entries((control.value.identifiers as Identifier[])
      .reduce((counter, identifier) => {
        const typeCount = {[identifier.type]: (identifier.type && counter[identifier.type]) ? ((counter[identifier.type] + 1)) : 1};
        return {
          ...counter,
          ...typeCount
        }
      }, {}))
      .filter(([type, count]: [string, number]) => (count > 1))
      .map(([type, count]: [string, number]) => type)
      .join(', ')

    return duplicateType ? {duplicateType: `Duplicate types ${duplicateType}. Types must be unique in a group`} : null;
  }

  public static identifierCodeValidator(control: AbstractControl): ValidationErrors | null {
    const codeValue: string = control?.value;
    const typeValue: string = control?.parent?.value?.type;

    if (!control || !codeValue || !typeValue) {
      return null;
    }

    return IdentifierPoolsValidators.validatorsMap[typeValue.toLowerCase()](control);
  }

  public static isinValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control?.value) {
        return null;
      }
      const code = control.value;
      const calculatedCheckDigit = IdentifierPoolsValidators.calculateIsinCheckDigit(code);
      const checkDigit = Number(code.charAt(code.length - 1));

      return calculatedCheckDigit === checkDigit ? null : {isin: 'Invalid ISIN code'};
    };
  }

  public static calculateIsinCheckDigit(isin: string): number {
    let digits: number[] = [];
    for (let char of isin) {
      if (/\d/.test(char)) {
        digits.push(parseInt(char, 10));
      } else {
        let value = char.charCodeAt(0) - 55;
        digits.push(Math.floor(value / 10));
        digits.push(value % 10);
      }
    }

    digits.pop();
    digits.reverse();

    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
      let digit = digits[i];
      if (i % 2 === 0) {
        digit *= 2;
        if (digit > 9) {
          digit = Math.floor(digit / 10) + (digit % 10);
        }
      }
      sum += digit;
    }

    return (10 - (sum % 10)) % 10;
  }
}
